<template>
  <div>
    <AlertDialog :open="showConfirmModal" @update:open="(o) => !o && (showConfirmModal = false)">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm sign out</AlertDialogTitle>
          <AlertDialogDescription>You will be logged out of this account.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter class="gap-2">
          <AlertDialogCancel @click="showConfirmModal = false">Cancel</AlertDialogCancel>
          <AlertDialogAction @click="signout()">Sign out</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <TopbarShell :collapsed="sidebar.collapsed" @toggle-sidebar="sidebar.toggle()">
      <template #title>
        <Breadcrumb>
          <BreadcrumbList>
            <template v-if="breadcrumb.items.length">
              <template v-for="(crumb, index) in breadcrumb.items" :key="index">
                <BreadcrumbItem>
                  <BreadcrumbLink
                    v-if="crumb.to && index < breadcrumb.items.length - 1"
                    class="cursor-pointer"
                    @click="$router.push(crumb.to)"
                  >{{ crumb.label }}</BreadcrumbLink>
                  <BreadcrumbPage v-else class="font-medium truncate">{{ crumb.label }}</BreadcrumbPage>
                </BreadcrumbItem>
                <BreadcrumbSeparator v-if="index < breadcrumb.items.length - 1" />
              </template>
            </template>
            <BreadcrumbItem v-else>
              <BreadcrumbPage class="font-medium">{{ pageTitle }}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </template>

      <template #actions>
        <div
          v-if="devMode === 'false'"
          class="h-9 flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3"
        >
          <span class="h-2 w-2 rounded-full bg-emerald-500" />
          <p class="text-foreground text-xs font-medium whitespace-nowrap">In development mode</p>
        </div>
        <div
          v-if="showSandboxOnly"
          class="h-9 flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3"
        >
          <p class="text-foreground text-xs font-medium">Sandbox mode</p>
          <Blocks class="w-4 h-4 text-muted-foreground" />
        </div>
        <div v-else-if="nonSandboxPages">
          <div v-if="customerContract != 'NOT_IN_CONTRACT'">
            <div
              :class="[
                testMode
                  ? 'border border-border bg-muted/60'
                  : 'border border-dashed border-border',
                'h-9 flex items-center gap-2 rounded-md px-3',
              ]"
            >
              <p class="text-foreground text-xs font-medium">Sandbox mode</p>
              <Switch :model-value="testMode" @update:model-value="toggleSandbox()" />
            </div>
          </div>
          <div v-else-if="preLaunchApprovedPages == true">
            <div
              :class="[
                testMode
                  ? 'border border-border bg-muted/60'
                  : 'border border-dashed border-border',
                'h-9 flex items-center gap-2 rounded-md px-3',
              ]"
            >
              <p class="text-foreground text-xs font-medium">Sandbox mode</p>
              <Switch :model-value="testMode" @update:model-value="toggleSandbox()" />
            </div>
          </div>
          <div
            v-else
            class="h-9 flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3"
          >
            <p class="text-foreground text-xs font-medium">Sandbox mode</p>
            <Blocks class="w-4 h-4 text-muted-foreground" />
          </div>
        </div>
      </template>
    </TopbarShell>
  </div>
</template>

<script>
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
  Switch,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  TopbarShell,
} from '@boogi/ui'
import { Blocks } from 'lucide-vue-next'
import { sidebarState } from '@/stores/sidebar'
import { breadcrumbState } from '@/stores/breadcrumb'
import { toast } from '@boogi/ui'

export default {
  name: 'TopBar',
  components: {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
    Switch,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
    TopbarShell,
    Blocks,
  },
  data() {
    return {
      sidebar: sidebarState,
      breadcrumb: breadcrumbState,
      email: localStorage.getItem('firstName') + ' ' + localStorage.getItem('lastName'),
      publisher: localStorage.getItem('publisher'),
      showConfirmModal: false,
      testMode: localStorage.getItem('sandboxMode') === 'true',
      devMode: process.env.VUE_APP_PRODUCTION,
      sandboxOnly: localStorage.getItem('sandbox') == 'true',
      notInContract: false,
    }
  },
  mounted() {
    window.addEventListener('foo-key-localstorage-changed', (event) => {
      this.email = event.detail.storage
    })
    const notice = sessionStorage.getItem('envSwitchNotice')
    if (notice) {
      sessionStorage.removeItem('envSwitchNotice')
      toast.success(
        notice === 'sandbox' ? 'You are now in sandbox mode' : 'You are now in production mode',
        {
          description:
            notice === 'sandbox'
              ? 'Data you see and create is sandbox data.'
              : 'You are working against live production data.',
        }
      )
    }
  },

  computed: {
    brandName() {
      return 'Test brand'
    },

    pageTitle() {
      const map = {
        '/': 'Home',
        '/cashback-program/program-offers': 'Your program',
        '/cashback-program/offer-catalogue': 'Offers catalogue',
        '/cashback-program/custom-offers': 'Custom offers',
        '/perks-and-rewards/product-catalogue': 'Perks',
        '/perks-and-rewards/inventory': 'Perks inventory',
        '/cashback-program/settlements': 'Settlements',
        '/perks-and-rewards/product-orders': 'Perks orders',
        '/enrolled-users/users': 'Users',
        '/enrolled-users/user-segments': 'User segments',
        '/transaction-processing': 'Transaction processing',
        '/transaction-processing/uploads': 'Transaction uploads',
        '/cashback-program/ledger': 'Cashback ledger',
        '/monitoring/bank-connections': 'Bank connections',
        '/developers/sdk-manager': 'Catalogue SDK',
        '/developers/api-keys': 'API keys',
        '/developers/webhooks': 'Webhooks',
        '/developers/playground': 'Playground',
        '/support': 'Support',
        '/settings': 'Settings',
        '/billing': 'Billing',
      }
      const p = this.$route.path
      if (map[p]) return map[p]
      const seg = p.split('/').filter(Boolean)[0] || 'Home'
      return seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, ' ')
    },

    showSandboxOnly() {
      // var publisherStatus = localStorage.getItem('publisherStatus') //
      if (this.devMode !== 'true') return false
      return this.sandboxOnly == true
    },

    nonSandboxPages() {
      // Show the sandbox/production toggle whenever a sandbox API is configured
      // (previously it only showed in production builds, so it was missing on
      // dev). Hidden on settings/billing, which are always production.
      if (!process.env.VUE_APP_SANDBOX_API_URL) return false
      var route = this.$route.fullPath
      if (route != '/settings' && route != '/billing') {
        return true
      } else {
        return false
      }
    },

    preLaunchApprovedPages() {
      var route = this.$route.fullPath
      if (
        route.includes('/retailer-catalogue') ||
        route.includes('/program-offers')
      ) {
        return true
      } else {
        return false
      }
    },

    customerContract() {
      var contract = localStorage.getItem('publisherStatus')
      return contract
    },
  },
  methods: {
    // The mode is tracked by an explicit `sandboxMode` flag rather than by comparing
    // apiUrl to VUE_APP_SANDBOX_API_URL: on dev both env vars point at the same host,
    // so that comparison was always true and the toggle could never leave sandbox.
    toggleSandbox() {
      const goingToSandbox = !this.testMode
      if (goingToSandbox) {
        localStorage.setItem('apiUrl', process.env.VUE_APP_SANDBOX_API_URL)
        localStorage.setItem('apiType', process.env.VUE_APP_SANDBOX_API_TYPE)
      } else {
        localStorage.setItem('apiUrl', process.env.VUE_APP_API_URL)
        localStorage.setItem('apiType', process.env.VUE_APP_API_TYPE)
      }
      localStorage.setItem('sandboxMode', String(goingToSandbox))
      this.testMode = goingToSandbox
      // The page reloads to refetch everything against the new environment, so the
      // confirmation is handed over in sessionStorage and shown once on the way back.
      sessionStorage.setItem('envSwitchNotice', goingToSandbox ? 'sandbox' : 'production')
      this.$router.go(0)
    },

    async signout() {
      // Revoke the token server-side BEFORE clearing storage. Clearing localStorage alone only
      // signs the user out of this browser — the idToken stays valid until its natural expiry, so
      // a copy of it could still be replayed. The endpoint deny-lists the token and signs the
      // Cognito session out globally.
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        if (token) {
          await this.$apiFetch(`${apiUrl}/private/api/client/user/logout`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          })
        }
      } catch (error) {
        // Never block sign-out on a failed revoke — the user must always be able to leave. The
        // token still expires on its own; this is best-effort hardening.
        console.error(error)
      }
      localStorage.clear()
      this.$router.push('/auth/sign-in')
    },
  },
}
</script>
